import { defineComponent as _defineComponent } from 'vue'
import { PropType, computed } from 'vue';
import VueI18n from 'vue-i18n';
import { defineMessages } from '@/lib/i18n';
import {
  SdsEditHistory,
  SdsEditHistoryDetail,
} from '@/interfaces/model/general/sds';
import { store } from '@/store/index';
import { readUserLocale } from '@/store/main/getters';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { formatToDateTime } from '@/utils/datetime';
import { groupingRules } from '@/lib/sds-edit-history/rule';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsEditHistoryEntry',
  props: {
  entry: {
    type: Object as PropType<SdsEditHistory>,
    required: true,
  },
  isFirst: {
    type: Boolean,
    default: false,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;



const $t = i18n.t.bind(i18n);
const locale = computed(() => readUserLocale(store));

const m = defineMessages({
  isLabelShown: 'ラベル表記あり',
  noArrayData: 'データなし',
  emptyValue: '—',
  section1ProductInfo: '1. 化学品及び会社情報（製品情報）',
  sdsSection1Manufacturer: '1.化学品及び会社情報(製造者)',
  sdsSection1Importer: '1.化学品及び会社情報(輸入者・販売者)',
  sdsSection2: '2.危険有害性の要約',
  sdsSection2Label: '2.GHSラベル',
  sdsSection3: '3.組成及び成分情報',
  sdsSection8: '8.曝露限界値',
  sdsSection8Acgih: '8.曝露限界値(ACGIH)',
  sdsSection8DnelDmel: '8.曝露限界値(DNEL・DMEL)',
  sdsSection8Jsoh:
    '8.曝露限界値(日本産業衛生学会 ; Japanese Society of Occupational Health)',
  sdsSection9: '9.物理的及び化学的性質',
  sdsSection9BoilingPoint: '9.物理的及び化学的性質(沸点)',
  sdsSection9FlashPoint: '9.物理的及び化学的性質(引火点)',
  sdsSection14: '14.輸送上の注意',
  sdsSection15: '15.適用法令',
  booleanNotApplicable: '該当しない',
});

const iconAndColorMapping: Record<string, { icon: string; color: string }> = {
  insert: {
    icon: 'mdi-plus',
    color: '#576EE5',
  },
  update: {
    icon: 'mdi-pencil-box-outline',
    color: 'primary',
  },
  delete: {
    icon: 'mdi-delete-forever-outline',
    color: 'error',
  },
};

const messageMapping = {
  sds_product: $t(m.section1ProductInfo),
  section1_manufacturer: $t(m.sdsSection1Manufacturer),
  section1_importer: $t(m.sdsSection1Importer),
  section2: $t(m.sdsSection2),
  section2_label: $t(m.sdsSection2Label),
  section2_hazard: $t(m.sdsSection2),
  section3: $t(m.sdsSection3),
  section3_material: $t(m.sdsSection3),
  section3_cas: $t(m.sdsSection3),
  section3_impurity_material: $t(m.sdsSection3),
  section3_impurity_cas: $t(m.sdsSection3),
  section8: $t(m.sdsSection8),
  // organization (ACGIH/JSOH/DNEL_DMEL) is now a column, not a separate
  // table/entity -- one shared title covers every organization.
  section8_exposure_control: $t(m.sdsSection8),
  section8_exposure_control_material: $t(m.sdsSection8),
  sds_section8: $t(m.sdsSection8),
  section9: $t(m.sdsSection9),
  section9_boiling_point: $t(m.sdsSection9BoilingPoint),
  section9_flash_point: $t(m.sdsSection9FlashPoint),
  section14: $t(m.sdsSection14),
  section15: $t(m.sdsSection15),
  section15_material: $t(m.sdsSection15),
  section15_cas: $t(m.sdsSection15),
};

// For backward compatibility
const booleanRules = {
  section2_label: {
    bomb: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    fire: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    fire_circle: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    gas: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    corrosion: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    toxic: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    exclamation: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    health: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
    environment: {
      true: $t(m.isLabelShown),
      false: $t(sharedMessages.isLabelNotShown),
    },
  },
  section3_material: {
    is_stated: {
      true: $t(sharedMessages.isStated),
      false: $t(sharedMessages.sdsNotStatedForm),
    },
  },
  section15: {
    applicable: {
      true: $t(sharedMessages.applicable),
      false: $t(m.booleanNotApplicable),
    },
  },
};

const enumRules = {
  section15: {
    applicable: {
      1: $t(sharedMessages.applicable),
      2: $t(sharedMessages.notApplicable),
      3: $t(sharedMessages.unclear),
      4: $t(sharedMessages.notStated),
    },
  },
};

const generateEditHistoryDetailTitle = (detail: SdsEditHistoryDetail) => {
  const key = detail.edit_entity as keyof typeof messageMapping;
  const mapped = messageMapping[key];
  if (mapped) return mapped;
  // Section 8 only: ensure "8.曝露限界値" when edit_entity is unknown (section may be number or string from API)
  const sectionNum = Number(detail.section);
  const isSection8 =
    sectionNum === 8 ||
    (detail.edit_entity &&
      String(detail.edit_entity).toLowerCase().includes('section8'));
  if (isSection8) {
    return $t(m.sdsSection8);
  }
  return '';
};

const generateEditHistoryDetailTarget = (detail: SdsEditHistoryDetail) => {
  if (detail.action === 'delete') {
    // For delete we only state what was removed (e.g. "AIHA"), not the column name
    return '';
  }
  if (detail.action === 'insert' && groupingRules[detail.edit_entity]) {
    return '';
  }
  // Prefer the human label (e.g. "区分") over the raw DB column name
  // (e.g. "category_id") when the backend has one for this column.
  return (
    pickLocalizedValue(
      detail.column_name,
      detail.column_name_ja,
      detail.column_name_en
    ) ?? ''
  );
};

// ✨ JSON配列を読みやすい形式にフォーマットするヘルパー関数
const formatJsonArray = (
  value: string | null | undefined
): VueI18n.TranslateResult => {
  if (!value) return $t(m.noArrayData);

  try {
    const parsed = JSON.parse(value);

    if (!Array.isArray(parsed)) {
      return value;
    }

    // 空配列の場合
    if (parsed.length === 0) {
      return $t(m.noArrayData);
    }

    // 配列の要素を読みやすく結合
    return parsed.join('、');
  } catch (error) {
    return $t(m.noArrayData);
  }
};

// Display value for null/empty; avoids showing literal "null" in edit history
const emptyDisplay = (): VueI18n.TranslateResult => $t(m.emptyValue);
const isNullOrEmpty = (v: string | null | undefined): boolean =>
  v == null || v === '' || String(v).toLowerCase() === 'null';

// Master-referencing columns (e.g. category_id) carry the raw id in
// before/after_value and the resolved name in before/after_value_ja / _en --
// prefer the name in the viewer's locale, falling back to the raw value for
// columns that never got a translation wired up.
const pickLocalizedValue = (
  raw: string | null | undefined,
  ja: string | null | undefined,
  en: string | null | undefined
): string | null | undefined => {
  if (locale.value === 'ja-JP') return ja || raw;
  return en || raw;
};

// True if value looks like an ISO datetime (e.g. "2026-02-05 08:28:53.827000+00:00")
const isIsoTimestamp = (v: string | null | undefined): boolean => {
  if (!v || typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}(T|\s)\d{2}:\d{2}/.test(v.trim());
};

const generateEditHistoryDetailText = (detail: SdsEditHistoryDetail) => {
  if (detail.action === 'delete') {
    // State only what was removed (e.g. "AIHA", "DNEL・DMEL") — use
    // value_prefix as the type/row label, but only for entities that are
    // actually grouped (e.g. a material, whose own name IS the prefix).
    // For an ungrouped entity (e.g. section2_hazard, or a standalone CAS
    // delete) value_prefix is someone else's context (the owning
    // material/item), not this row's own identity, so showing the real
    // before_value is more useful.
    if (groupingRules[detail.edit_entity] && detail.value_prefix) {
      return detail.value_prefix;
    }
    // If only before_value is a timestamp (e.g. deleted_at), don't show it — we already stated the type above
    const deletedValue = pickLocalizedValue(
      detail.before_value,
      detail.before_value_ja,
      detail.before_value_en
    );
    if (deletedValue && !isIsoTimestamp(deletedValue)) return deletedValue;
    return '';
  }
  if (detail.action === 'insert' && groupingRules[detail.edit_entity]) {
    return detail.value_prefix;
  }

  if (detail.column_type === 'boolean') {
    if (booleanRules[detail.edit_entity as keyof typeof booleanRules]) {
      const rule =
        booleanRules[detail.edit_entity as keyof typeof booleanRules];
      if (rule[detail.raw_column_name as keyof typeof rule]) {
        const value = rule[detail.raw_column_name as keyof typeof rule];
        return value[detail.after_value as keyof typeof value];
      }
    }
    return detail.after_value === 'true'
      ? $t(sharedMessages.enabled)
      : $t(sharedMessages.disabled);
  } else {
    if (enumRules[detail.edit_entity as keyof typeof enumRules]) {
      const rule = enumRules[detail.edit_entity as keyof typeof enumRules];
      if (rule[detail.raw_column_name as keyof typeof rule]) {
        const value = rule[detail.raw_column_name as keyof typeof rule];
        return value[Number(detail.after_value) as keyof typeof value];
      }
    }
  }

  // ✨ 配列型の場合はJSON配列をパースして表示
  if (detail.column_type === 'array') {
    return formatJsonArray(detail.after_value);
  }

  const afterValue = pickLocalizedValue(
    detail.after_value,
    detail.after_value_ja,
    detail.after_value_en
  );
  const afterDisplay = isNullOrEmpty(afterValue) ? emptyDisplay() : afterValue;
  // Compare against the resolved (ja/en) value, not the raw one -- a
  // master-referencing column's own value_prefix and after_value can both
  // resolve to the same name (e.g. item_id's own line), which would
  // otherwise show it twice ("高圧ガス - 高圧ガス").
  if (detail.value_prefix && detail.value_prefix !== afterValue) {
    return `${detail.value_prefix} - ${afterDisplay}`;
  }
  return afterDisplay;
};

return { __sfc: true,props, $t, locale, m, iconAndColorMapping, messageMapping, booleanRules, enumRules, generateEditHistoryDetailTitle, generateEditHistoryDetailTarget, formatJsonArray, emptyDisplay, isNullOrEmpty, pickLocalizedValue, isIsoTimestamp, generateEditHistoryDetailText, formatToDateTime }
}

})